import React from "react"
import { Bar, BarChart, XAxis, YAxis, Tooltip, Legend } from "recharts"
import { Card, CardContent, CardHeader, CardTitle } from "~/shadcn/ui/card"
import { ChartContainer } from "~/shadcn/ui/chart"

export type StatusPoint = {
  bucket: string
  ok: number
  rateLimited: number
  badRequest: number
  timeout: number
}

const STATUS_CONFIG = {
  ok: { label: "200 OK", color: "#22c55e" },
  rateLimited: { label: "429 Rate Limited", color: "#f59e0b" },
  badRequest: { label: "400 Bad Request", color: "#6b7280" },
  timeout: { label: "5xx Timeout", color: "#ef4444" },
} as const

function formatBucketLabel(bucket: string): string {
  const date = new Date(bucket)
  const now = new Date()
  const diffDays = (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24)
  if (diffDays < 2) {
    return date.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
    })
  }
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" })
}

export const VolumeChart: React.FC<{ data: StatusPoint[] }> = ({ data }) => {
  const chartData = data.map((point) => ({
    label: formatBucketLabel(point.bucket),
    ok: point.ok,
    rateLimited: point.rateLimited,
    badRequest: point.badRequest,
    timeout: point.timeout,
  }))

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-gray-500">
          Request Volume by Status Code
        </CardTitle>
      </CardHeader>
      <CardContent>
        {data.length === 0 ? (
          <div className="flex h-[280px] items-center justify-center text-sm text-gray-400">
            No data for this period
          </div>
        ) : (
          <ChartContainer
            className="h-[280px] w-full"
            config={Object.fromEntries(
              Object.entries(STATUS_CONFIG).map(([k, v]) => [
                k,
                { label: v.label, color: v.color },
              ])
            )}
          >
            <BarChart
              data={chartData}
              margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
            >
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11 }}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                tick={{ fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                allowDecimals={false}
              />
              <Tooltip
                content={({ payload, label }) => {
                  if (!payload?.length) return null
                  const total = payload.reduce(
                    (s, e) => s + Number(e.value ?? 0),
                    0
                  )
                  return (
                    <div className="rounded border bg-white px-3 py-2 text-xs shadow-sm space-y-1">
                      <div className="font-medium">{label}</div>
                      {payload.map((entry) => (
                        <div
                          key={entry.dataKey as string}
                          className="flex items-center gap-2"
                        >
                          <div
                            className="h-2 w-2 rounded-full"
                            style={{
                              backgroundColor:
                                STATUS_CONFIG[
                                  entry.dataKey as keyof typeof STATUS_CONFIG
                                ]?.color,
                            }}
                          />
                          <span>
                            {
                              STATUS_CONFIG[
                                entry.dataKey as keyof typeof STATUS_CONFIG
                              ]?.label
                            }
                            :
                          </span>
                          <span className="font-medium">
                            {Number(entry.value ?? 0).toLocaleString()}
                          </span>
                          <span className="text-gray-400">
                            (
                            {total > 0
                              ? (
                                  (Number(entry.value ?? 0) / total) *
                                  100
                                ).toFixed(0)
                              : 0}
                            %)
                          </span>
                        </div>
                      ))}
                      <div className="border-t pt-1 font-medium">
                        Total: {total.toLocaleString()}
                      </div>
                    </div>
                  )
                }}
              />
              <Legend
                formatter={(value) =>
                  STATUS_CONFIG[value as keyof typeof STATUS_CONFIG]?.label ??
                  value
                }
                wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
              />
              {(
                Object.entries(STATUS_CONFIG) as [
                  keyof typeof STATUS_CONFIG,
                  { label: string; color: string },
                ][]
              ).map(([key, cfg]) => (
                <Bar
                  key={key}
                  dataKey={key}
                  name={key}
                  stackId="a"
                  fill={cfg.color}
                  isAnimationActive={false}
                />
              ))}
            </BarChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  )
}
